import { Trans } from '@lingui/macro'
import { sendAnalyticsEvent, Trace, TraceEvent, useTrace } from '@uniswap/analytics'
import {
  BrowserEvent,
  InterfaceElementName,
  InterfaceEventName,
  InterfacePageName,
  InterfaceSectionName,
  SwapEventName,
} from '@uniswap/analytics-events'
import { Currency, CurrencyAmount, Percent, Token } from '@uniswap/sdk-core'
import { useWeb3React } from '@web3-react/core'
import { useToggleAccountDrawer } from 'components/AccountDrawer'
//import { sendEvent } from 'components/analytics'
import { NetworkAlert } from 'components/NetworkAlert/NetworkAlert'
import PriceImpactWarning from 'components/swap/PriceImpactWarning'
import SwapDetailsDropdown from 'components/swap/SwapDetailsDropdown'
import TokenSafetyModal from 'components/TokenSafety/TokenSafetyModal'
import { PITEAS_ROUTER_ADDRESS } from 'constants/addresses'
//import { UNIVERSAL_ROUTER_ADDRESS } from 'constants/addresses'
import { getChainInfo } from 'constants/chainInfo'
import { isSupportedChain, SupportedChainId } from 'constants/chains'
import useENSAddress from 'hooks/useENSAddress'
import { useMaxAmountIn } from 'hooks/useMaxAmountIn'
import usePermit2Allowance, { AllowanceState } from 'hooks/usePermit2Allowance'
import usePrevious from 'hooks/usePrevious'
import { useSwapCallback } from 'hooks/useSwapCallback'
import { useUSDPrice } from 'hooks/useUSDPrice'
import JSBI from 'jsbi'
import { formatSwapQuoteReceivedEventProperties } from 'lib/utils/analytics'
import { ReactNode, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { ArrowDown } from 'react-feather'
import { RefreshCw } from 'react-feather'
import { useNavigate } from 'react-router-dom'
import { Text } from 'rebass'
import { InterfaceTrade, TradeState } from 'state/routing/types'
import styled, { useTheme } from 'styled-components/macro'
import { flexRowNoWrap } from 'theme/styles'
import { Z_INDEX } from 'theme/zIndex'
import { currencyAmountToPreciseFloat, formatTransactionAmount } from 'utils/formatNumbers'
import { didUserReject } from 'utils/swapErrorToUserReadableMessage'
import { switchChain } from 'utils/switchChain'
import { ButtonError, ButtonLight, ButtonPrimary, ButtonFetch } from '../../components/Button'
import { AutoColumn } from '../../components/Column'
import SwapCurrencyInputPanel from '../../components/CurrencyInputPanel/SwapCurrencyInputPanel'
import NavBar from '../../components/NavBar'
import ReceiverCollapsed from '../../components/swap/ReceiverCollapsed'
import ReceiverSection from '../../components/swap/ReceiverSection'
import ReceiverWarningModal from '../../components/swap/ReceiverWarningModal'
import confirmPriceImpactWithoutFee from '../../components/swap/confirmPriceImpactWithoutFee'
import ConfirmSwapModal from '../../components/swap/ConfirmSwapModal'
import {
  ArrowWrapper,
  PageWrapper,
  RoutePanel,
  SwapCallbackError,
  SwapCardColumn,
  SwapLayout,
  SwapWrapper,
} from '../../components/swap/styleds'
import SwapHeader from '../../components/swap/SwapHeader'
import SwapRoute from '../../components/swap/SwapRoute'
import { SwitchLocaleLink } from '../../components/SwitchLocaleLink'
import { getSwapCurrencyId, TOKEN_SHORTHANDS } from '../../constants/tokens'
import { useCurrency, useDefaultActiveTokens } from '../../hooks/Tokens'
import { useIsSwapUnsupported } from '../../hooks/useIsSwapUnsupported'
import useWrapCallback, { WrapErrorText, WrapType } from '../../hooks/useWrapCallback'
import { Field, replaceSwapState } from '../../state/swap/actions'
import { useDefaultsFromURLSearch, useDerivedSwapInfo, useSwapActionHandlers } from '../../state/swap/hooks-accountAPIbefore'
import swapReducer, { initialState as initialSwapState, SwapState } from '../../state/swap/reducer'
import { useExpertModeManager } from '../../state/user/hooks'
import { ThemedText } from '../../theme'
import { computeFiatValuePriceImpact } from '../../utils/computeFiatValuePriceImpact'
import { maxAmountSpend } from '../../utils/maxAmountSpend'
import { computeRealizedPriceImpact, warningSeverity } from '../../utils/prices'
import { supportedChainId } from '../../utils/supportedChainId'

const RefetchButton = styled(RefreshCw)`
  height: 15px;
  width: 15px;
  transform: rotate(-30deg);
  margin-left: 5px;
  font-weight: bold;
  > * {
    stroke: ${({ theme }) => theme.textSecondary};
    stroke-width: 2;
  }
`
export const ArrowContainer = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  width: 100%;
  height: 100%;
`
const HeaderWrapper = styled.div<{ transparent?: boolean }>`
  ${flexRowNoWrap};
  background-color: ${({ theme, transparent }) => !transparent && theme.backgroundSurface};
  border-bottom: ${({ theme, transparent }) => !transparent && `1px solid ${theme.backgroundOutline}`};
  width: 100%;
  justify-content: space-between;
  position: fixed;
  top: 0;
  z-index: ${Z_INDEX.dropdown};
  align-items: center;
  max-width: 440px;
  padding: 0 4px;
`

const SwapSection = styled.div`
  position: relative;
  background-color: ${({ theme }) => theme.backgroundModule};
  border-radius: 6px;
  padding: 16px 12px;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 15px;
  line-height: 20px;
  font-weight: 500;

  &:before {
    box-sizing: border-box;
    background-size: 100%;
    border-radius: inherit;

    position: absolute;
    top: 0;
    left: 0;

    width: 100%;
    height: 100%;
    pointer-events: none;
    content: '';
    border: 1px solid ${({ theme }) => theme.backgroundModule};
  }

  &:hover:before {
    border-color: ${({ theme }) => theme.stateOverlayHover};
  }

  &:focus-within:before {
    border-color: ${({ theme }) => theme.stateOverlayPressed};
  }
`

const OutputSwapSection = styled(SwapSection)`
  border-bottom: ${({ theme }) => `1px solid ${theme.backgroundSurface}`};
`

function getIsValidSwapQuote(
  trade: InterfaceTrade | undefined,
  tradeState: TradeState,
  swapInputError?: ReactNode
): boolean {
  return Boolean(swapInputError && trade && tradeState === TradeState.VALID)
}

function largerPercentValue(a?: Percent, b?: Percent) {
  if (a && b) {
    return a.greaterThan(b) ? a : b
  } else if (a) {
    return a
  } else if (b) {
    return b
  }
  return undefined
}

export default function SwapPage({ className }: { className?: string }) {
  const { chainId: connectedChainId } = useWeb3React()
  const loadedUrlParams = useDefaultsFromURLSearch()
  const [isRoutePanelOpen, setIsRoutePanelOpen] = useState(false)

  return (
    <Trace page={InterfacePageName.SWAP_PAGE} shouldLogImpression>
      <PageWrapper $wide={isRoutePanelOpen}>
        <HeaderWrapper transparent={true}>
          <NavBar blur={false} />
        </HeaderWrapper>
        <Swap
          className={className}
          chainId={connectedChainId}
          prefilledState={{
            [Field.INPUT]: { currencyId: loadedUrlParams?.[Field.INPUT]?.currencyId },
            [Field.OUTPUT]: { currencyId: loadedUrlParams?.[Field.OUTPUT]?.currencyId },
          }}
          routePanelOpen={isRoutePanelOpen}
          onRoutePanelOpenChange={setIsRoutePanelOpen}
        />
      </PageWrapper>
      <SwitchLocaleLink />
    </Trace>
  )
}

/**
 * The swap component displays the swap interface, manages state for the swap, and triggers onchain swaps.
 *
 * In most cases, chainId should refer to the connected chain, i.e. `useWeb3React().chainId`.
 * However if this component is being used in a context that displays information from a different, unconnected
 * chain (e.g. the TDP), then chainId should refer to the unconnected chain.
 */
export function Swap({
  className,
  prefilledState = {},
  chainId,
  onCurrencyChange,
  disableTokenInputs = false,
  routePanelOpen = false,
  onRoutePanelOpenChange,
}: {
  className?: string
  prefilledState?: Partial<SwapState>
  chainId?: SupportedChainId
  onCurrencyChange?: (selected: Pick<SwapState, Field.INPUT | Field.OUTPUT>) => void
  disableTokenInputs?: boolean
  routePanelOpen?: boolean
  onRoutePanelOpenChange?: (open: boolean) => void
}) {
  const { account, chainId: connectedChainId, connector } = useWeb3React()
  const trace = useTrace()

  // token warning stuff
  const prefilledInputCurrency = useCurrency(prefilledState?.[Field.INPUT]?.currencyId)
  const prefilledOutputCurrency = useCurrency(prefilledState?.[Field.OUTPUT]?.currencyId)

  const [loadedInputCurrency, setLoadedInputCurrency] = useState(prefilledInputCurrency)
  const [loadedOutputCurrency, setLoadedOutputCurrency] = useState(prefilledOutputCurrency)

  useEffect(() => {
    setLoadedInputCurrency(prefilledInputCurrency)
    setLoadedOutputCurrency(prefilledOutputCurrency)
  }, [prefilledInputCurrency, prefilledOutputCurrency])

  const [dismissTokenWarning, setDismissTokenWarning] = useState<boolean>(false)
  const urlLoadedTokens: Token[] = useMemo(
    () => [loadedInputCurrency, loadedOutputCurrency]?.filter((c): c is Token => c?.isToken ?? false) ?? [],
    [loadedInputCurrency, loadedOutputCurrency]
  )
  const handleConfirmTokenWarning = useCallback(() => {
    setDismissTokenWarning(true)
  }, [])

  // dismiss warning if all imported tokens are in active lists
  const defaultTokens = useDefaultActiveTokens(chainId)
  const importTokensNotInDefault = useMemo(
    () =>
      urlLoadedTokens &&
      urlLoadedTokens
        .filter((token: Token) => {
          return !(token.address in defaultTokens)
        })
        .filter((token: Token) => {
          // Any token addresses that are loaded from the shorthands map do not need to show the import URL
          const supported = supportedChainId(chainId)
          if (!supported) return true
          return !Object.keys(TOKEN_SHORTHANDS).some((shorthand) => {
            const shorthandTokenAddress = TOKEN_SHORTHANDS[shorthand][supported]
            return shorthandTokenAddress && shorthandTokenAddress === token.address
          })
        }),
    [chainId, defaultTokens, urlLoadedTokens]
  )

  const theme = useTheme()

  // toggle wallet when disconnected
  const toggleWalletDrawer = useToggleAccountDrawer()

  // for expert mode
  const [isExpertMode] = useExpertModeManager()
  // swap state
  const [state, dispatch] = useReducer(swapReducer, { ...initialSwapState, ...prefilledState })
  const { typedValue, recipient, independentField } = state

  const previousConnectedChainId = usePrevious(connectedChainId)
  const previousPrefilledState = usePrevious(prefilledState)
  useEffect(() => {
    const combinedInitialState = { ...initialSwapState, ...prefilledState }
    const chainChanged = previousConnectedChainId && previousConnectedChainId !== connectedChainId
    const prefilledInputChanged =
      previousPrefilledState &&
      previousPrefilledState?.[Field.INPUT]?.currencyId !== prefilledState?.[Field.INPUT]?.currencyId
    const prefilledOutputChanged =
      previousPrefilledState &&
      previousPrefilledState?.[Field.OUTPUT]?.currencyId !== prefilledState?.[Field.OUTPUT]?.currencyId
    if (chainChanged || prefilledInputChanged || prefilledOutputChanged) {
      dispatch(
        replaceSwapState({
          ...initialSwapState,
          ...prefilledState,
          field: combinedInitialState.independentField ?? Field.INPUT,
          inputCurrencyId: combinedInitialState.INPUT.currencyId ?? undefined,
          outputCurrencyId: combinedInitialState.OUTPUT.currencyId ?? undefined,
        })
      )
    }
  }, [connectedChainId, prefilledState, previousConnectedChainId, previousPrefilledState])

  const {
    trade: { state: tradeState, trade, refetchQuote, isFetching: isFetchingTrade },
    allowedSlippage,
    autoSlippage,
    currencyBalances,
    parsedAmount,
    currencies,
    inputError: swapInputError,
  } = useDerivedSwapInfo(state, chainId)

  const {
    wrapType,
    execute: onWrap,
    inputError: wrapInputError,
  } = useWrapCallback(currencies[Field.INPUT], currencies[Field.OUTPUT], typedValue)
  const showWrap: boolean = wrapType !== WrapType.NOT_APPLICABLE
  const { address: recipientAddress } = useENSAddress(recipient)

  const parsedAmounts = useMemo(
    () =>
      showWrap
        ? {
            [Field.INPUT]: parsedAmount,
            [Field.OUTPUT]: parsedAmount,
          }
        : {
            [Field.INPUT]: independentField === Field.INPUT ? parsedAmount : trade?.inputAmount,
            [Field.OUTPUT]: independentField === Field.OUTPUT ? parsedAmount : trade?.outputAmount,
          },
    [independentField, parsedAmount, showWrap, trade]
  )

  const fiatValueInput = useUSDPrice(parsedAmounts[Field.INPUT])
  const fiatValueOutput = useUSDPrice(parsedAmounts[Field.OUTPUT])
  //console.log('xccc', fiatValueInput, fiatValueOutput)
  const showFiatValueInput = Boolean(parsedAmounts[Field.INPUT])
  const showFiatValueOutput = Boolean(parsedAmounts[Field.OUTPUT])

  const [routeNotFound, routeIsLoading, routeIsSyncing] = useMemo(
    () => [!trade?.swaps, TradeState.LOADING === tradeState, TradeState.LOADING === tradeState && Boolean(trade)],
    [trade, tradeState]
  )

  const routeIsRefreshing = useMemo(
    () => Boolean(trade) && (tradeState === TradeState.LOADING || isFetchingTrade),
    [trade, tradeState, isFetchingTrade]
  )

  const fiatValueTradeInput = useUSDPrice(trade?.inputAmount)
  const fiatValueTradeOutput = useUSDPrice(trade?.outputAmount)
  //console.log('xccd', fiatValueTradeInput, fiatValueTradeOutput)
  const stablecoinPriceImpact = useMemo(
    () =>
      routeIsSyncing || !trade
        ? undefined
        : computeFiatValuePriceImpact(fiatValueTradeInput.data, fiatValueTradeOutput.data),
    [fiatValueTradeInput, fiatValueTradeOutput, routeIsSyncing, trade]
  )

  useEffect(() => {
    //if (!trade) return
    trade && stablecoinPriceImpact && trade.priceImpactUSD(stablecoinPriceImpact)
  }, [trade, stablecoinPriceImpact])

  const { onSwitchTokens, onCurrencySelection, onUserInput, onChangeRecipient } = useSwapActionHandlers(dispatch)
  const isValid = !swapInputError
  const dependentField: Field = independentField === Field.INPUT ? Field.OUTPUT : Field.INPUT

  const handleTypeInput = useCallback(
    (value: string) => {
      onUserInput(Field.INPUT, value)
    },
    [onUserInput]
  )
  const handleTypeOutput = useCallback(
    (value: string) => {
      onUserInput(Field.OUTPUT, value)
    },
    [onUserInput]
  )

  const navigate = useNavigate()
  const swapIsUnsupported = useIsSwapUnsupported(currencies[Field.INPUT], currencies[Field.OUTPUT])

  // reset if they close warning without tokens in params
  const handleDismissTokenWarning = useCallback(() => {
    setDismissTokenWarning(true)
    navigate('/swap/')
  }, [navigate])

  // modal and loading
  const [{ showConfirm, tradeToConfirm, swapError, txHash }, setSwapState] = useState<{
    showConfirm: boolean
    tradeToConfirm?: InterfaceTrade
    attemptingTxn: boolean
    swapError?: Error
    txHash?: string
  }>({
    showConfirm: false,
    tradeToConfirm: undefined,
    attemptingTxn: false,
    swapError: undefined,
    txHash: undefined,
  })

  // receiver toggle state
  const [receiverEnabled, setReceiverEnabled] = useState(false)
  const [showRecipientInput, setShowRecipientInput] = useState(false)
  const [showRecipientWarning, setShowRecipientWarning] = useState(false)
  const [pendingRecipient, setPendingRecipient] = useState<string | null>(null)
  const previousRecipientRef = useRef<string | null>(recipient)

  const changeRecipient = useCallback(
    (value: string | null) => {
      if (value !== recipient) {
        previousRecipientRef.current = recipient
      }
      onChangeRecipient(value)
    },
    [onChangeRecipient, recipient]
  )

  const normalizedAccount = account?.toLowerCase()
  const normalizedRecipient = recipient ? recipient.toLowerCase() : null
  const hasCustomRecipient = Boolean(
    receiverEnabled && normalizedRecipient && (!normalizedAccount || normalizedRecipient !== normalizedAccount)
  )
  const customRecipient = hasCustomRecipient && recipient ? recipient : null
  const recipientHex = customRecipient?.toLowerCase().replace(/^0x/, '')
  const recipientMatchesCalldata = useMemo(() => {
    if (!recipientHex) {
      return true
    }
    const methodParams = trade?.methodParameters
    if (!methodParams) {
      return true
    }
    const { calldata, value } = methodParams
    if (typeof calldata !== 'string' && typeof value !== 'string') {
      return true
    }
    const lowerRecipient = recipientHex.toLowerCase()
    const shouldCheckCalldata = typeof calldata === 'string' && calldata.length <= 50000
    if (shouldCheckCalldata && calldata.toLowerCase().includes(lowerRecipient)) {
      return true
    }
    const shouldCheckValue = typeof value === 'string' && value.length <= 50000
    if (shouldCheckValue && value.toLowerCase().includes(lowerRecipient)) {
      return true
    }
    return !(shouldCheckCalldata || shouldCheckValue)
  }, [recipientHex, trade?.methodParameters])

  const disableReceiver = useCallback(() => {
    setReceiverEnabled(false)
    setShowRecipientInput(false)
    setShowRecipientWarning(false)
    const hadRecipient = recipient !== null
    setPendingRecipient(null)
    if (hadRecipient) {
      changeRecipient(null)
      previousRecipientRef.current = null
      refetchQuote?.()
    }
  }, [changeRecipient, recipient, refetchQuote])

  const enableReceiver = useCallback(() => {
    setReceiverEnabled(true)
    setShowRecipientInput(true)
    setPendingRecipient(null)
  }, [])

  const handleToggleReceiverSetting = useCallback(() => {
    if (receiverEnabled) {
      disableReceiver()
    } else {
      enableReceiver()
    }
  }, [receiverEnabled, disableReceiver, enableReceiver])

  const handleRemoveRecipient = useCallback(() => {
    changeRecipient(null)
    previousRecipientRef.current = null
    setShowRecipientInput(true)
    setPendingRecipient(null)
    refetchQuote?.()
  }, [changeRecipient, refetchQuote])

  const handleOpenRecipientWarning = useCallback((newRecipient: string) => {
    setPendingRecipient(newRecipient)
    setShowRecipientWarning(true)
  }, [])

  const handleCloseRecipientPanel = useCallback(() => {
    setShowRecipientInput(false)
    setPendingRecipient(null)
    setShowRecipientWarning(false)
  }, [])

  const handleConfirmRecipientWarning = useCallback(() => {
    setShowRecipientWarning(false)
    if (pendingRecipient) {
      changeRecipient(pendingRecipient)
      previousRecipientRef.current = pendingRecipient
      setPendingRecipient(null)
      refetchQuote?.()
    }
  }, [changeRecipient, pendingRecipient, refetchQuote])

  const handleDismissRecipientWarning = useCallback(() => {
    setShowRecipientWarning(false)
    if (pendingRecipient) {
      const fallback = previousRecipientRef.current ?? account ?? null
      changeRecipient(fallback)
      previousRecipientRef.current = fallback
      setPendingRecipient(null)
    }
  }, [account, changeRecipient, pendingRecipient])

  const formattedAmounts = useMemo(
    () => ({
      [independentField]: typedValue,
      [dependentField]: showWrap
        ? parsedAmounts[independentField]?.toExact() ?? ''
        : formatTransactionAmount(currencyAmountToPreciseFloat(parsedAmounts[dependentField])),
    }),
    [dependentField, independentField, parsedAmounts, showWrap, typedValue]
  )

  const userHasSpecifiedInputOutput = Boolean(
    currencies[Field.INPUT] && currencies[Field.OUTPUT] && parsedAmounts[independentField]?.greaterThan(JSBI.BigInt(0))
  )

  const maximumAmountIn = useMaxAmountIn(trade, allowedSlippage)
  const allowance = usePermit2Allowance(
    maximumAmountIn ??
      (parsedAmounts[Field.INPUT]?.currency.isToken
        ? (parsedAmounts[Field.INPUT] as CurrencyAmount<Token>)
        : undefined),
    isSupportedChain(chainId) ? PITEAS_ROUTER_ADDRESS : undefined
  )
  //console.log("allowance",allowance)
  const maxInputAmount: CurrencyAmount<Currency> | undefined = useMemo(
    () => maxAmountSpend(currencyBalances[Field.INPUT]),
    [currencyBalances]
  )
  const showMaxButton = Boolean(maxInputAmount?.greaterThan(0) && !parsedAmounts[Field.INPUT]?.equalTo(maxInputAmount))
  const swapFiatValues = useMemo(() => {
    return { amountIn: fiatValueTradeInput.data, amountOut: fiatValueTradeOutput.data }
  }, [fiatValueTradeInput, fiatValueTradeOutput])

  // the callback to execute the swap
  //console.log(trade)

  const { callback: swapCallback } = useSwapCallback(
    trade,
    swapFiatValues,
    allowedSlippage,
    allowance.state === AllowanceState.ALLOWED ? allowance.permitSignature : undefined
  )

  const handleSwap = useCallback(() => {
    if (!swapCallback) {
      return
    }
    if (stablecoinPriceImpact && !confirmPriceImpactWithoutFee(stablecoinPriceImpact)) {
      return
    }
    setSwapState((currentState) => ({
      ...currentState,
      attemptingTxn: true,
      swapError: undefined,
      txHash: undefined,
    }))
    swapCallback()
      .then((hash) => {
        setSwapState((currentState) => ({
          ...currentState,
          attemptingTxn: false,
          swapError: undefined,
          txHash: hash,
        }))
        /*sendEvent({
          category: 'Swap',
          action: 'transaction hash',
          label: hash,
        })
        sendEvent({
          category: 'Swap',
          action:
            recipient === null
              ? 'Swap w/o Send'
              : (recipientAddress ?? recipient) === account
              ? 'Swap w/o Send + recipient'
              : 'Swap w/ Send',
          label: [TRADE_STRING, trade?.inputAmount?.currency?.symbol, trade?.outputAmount?.currency?.symbol, 'MH'].join(
            '/'
          ),
        })*/
      })
      .catch((error) => {
        setSwapState((currentState) => ({
          ...currentState,
          attemptingTxn: false,
          swapError: error,
          txHash: undefined,
        }))
      })
  }, [swapCallback, stablecoinPriceImpact])

  // errors
  const [swapQuoteReceivedDate, setSwapQuoteReceivedDate] = useState<Date | undefined>()

  // warnings on the greater of fiat value price impact and execution price impact
  const { priceImpactSeverity, largerPriceImpact } = useMemo(() => {
    const marketPriceImpact = trade?.priceImpact ? computeRealizedPriceImpact(trade) : undefined
    const largerPriceImpact = largerPercentValue(marketPriceImpact, stablecoinPriceImpact)
    return { priceImpactSeverity: warningSeverity(largerPriceImpact), largerPriceImpact }
  }, [stablecoinPriceImpact, trade])

  const handleConfirmDismiss = useCallback(() => {
    setSwapState((currentState) => ({ ...currentState, showConfirm: false }))
    // if there was a tx hash, we want to clear the input
    if (txHash) {
      onUserInput(Field.INPUT, '')
    }
  }, [onUserInput, txHash])

  const handleAcceptChanges = useCallback(() => {
    setSwapState((currentState) => ({ ...currentState, tradeToConfirm: trade }))
  }, [trade])

  const handleInputSelect = useCallback(
    (inputCurrency: Currency) => {
      onCurrencySelection(Field.INPUT, inputCurrency)
      onCurrencyChange?.({
        [Field.INPUT]: {
          currencyId: getSwapCurrencyId(inputCurrency),
        },
        [Field.OUTPUT]: state[Field.OUTPUT],
      })
    },
    [onCurrencyChange, onCurrencySelection, state]
  )

  const handleMaxInput = useCallback(() => {
    maxInputAmount && onUserInput(Field.INPUT, maxInputAmount.toExact())
    /*sendEvent({
      category: 'Swap',
      action: 'Max',
    })*/
  }, [maxInputAmount, onUserInput])

  const handleOutputSelect = useCallback(
    (outputCurrency: Currency) => {
      onCurrencySelection(Field.OUTPUT, outputCurrency)
      onCurrencyChange?.({
        [Field.INPUT]: state[Field.INPUT],
        [Field.OUTPUT]: {
          currencyId: getSwapCurrencyId(outputCurrency),
        },
      })
    },
    [onCurrencyChange, onCurrencySelection, state]
  )

  const priceImpactTooHigh = priceImpactSeverity > 3 && !isExpertMode
  const showPriceImpactWarning = largerPriceImpact && priceImpactSeverity > 3

  const prevTrade = usePrevious(trade)
  useEffect(() => {
    if (!trade || prevTrade === trade) return // no new swap quote to log

    setSwapQuoteReceivedDate(new Date())
    sendAnalyticsEvent(SwapEventName.SWAP_QUOTE_RECEIVED, {
      ...formatSwapQuoteReceivedEventProperties(trade, trade.gasUseEstimateUSD ?? undefined),
      ...trace,
    })
  }, [prevTrade, trade, trace])

  const hasRouteContext = Boolean(trade || routeIsLoading || routeIsSyncing)
  const showDetailsDropdown = Boolean(!showWrap && userHasSpecifiedInputOutput && hasRouteContext)

  const canShowRoutePanel = hasRouteContext || routePanelOpen

  const handleRoutePanelToggle = useCallback(() => {
    if (!onRoutePanelOpenChange) {
      return
    }
    if (!routePanelOpen && !hasRouteContext) {
      return
    }
    onRoutePanelOpenChange(!routePanelOpen)
  }, [hasRouteContext, onRoutePanelOpenChange, routePanelOpen])

  const handleRoutePanelClose = useCallback(() => {
    if (!onRoutePanelOpenChange) {
      return
    }
    onRoutePanelOpenChange(false)
  }, [onRoutePanelOpenChange])

  return (
    <SwapLayout $routeOpen={routePanelOpen}>
      <SwapCardColumn>
        <SwapWrapper chainId={chainId} className={className} id="swap-page">
          <TokenSafetyModal
            isOpen={importTokensNotInDefault.length > 0 && !dismissTokenWarning}
            tokenAddress={importTokensNotInDefault[0]?.address}
            secondTokenAddress={importTokensNotInDefault[1]?.address}
            onContinue={handleConfirmTokenWarning}
            onCancel={handleDismissTokenWarning}
            showCancel={true}
          />
          <SwapHeader
            autoSlippage={autoSlippage}
            inputCurrency={currencies[Field.INPUT]}
            outputCurrency={currencies[Field.OUTPUT]}
            refetchQuote={refetchQuote}
            isLoadingTrade={isFetchingTrade}
            hasQuote={!!trade}
            receiverEnabled={receiverEnabled}
            onToggleReceiver={handleToggleReceiverSetting}
            routePanelOpen={routePanelOpen}
            onToggleRoutePanel={handleRoutePanelToggle}
            routePanelEnabled={canShowRoutePanel}
            routePending={routeIsLoading || routeIsSyncing}
            hasRoute={Boolean(trade)}
          />
          <ReceiverWarningModal
            isOpen={showRecipientWarning}
            onDismiss={handleDismissRecipientWarning}
            onConfirm={handleConfirmRecipientWarning}
            recipientAddress={pendingRecipient || recipientAddress || recipient || ''}
          />

          {trade && showConfirm && (
            <ConfirmSwapModal
              trade={trade}
              originalTrade={tradeToConfirm}
              onAcceptChanges={handleAcceptChanges}
              txHash={txHash}
              allowedSlippage={allowedSlippage}
              onConfirm={handleSwap}
              allowance={allowance}
              swapError={swapError}
              onDismiss={handleConfirmDismiss}
              swapQuoteReceivedDate={swapQuoteReceivedDate}
              fiatValueInput={fiatValueTradeInput}
              fiatValueOutput={fiatValueTradeOutput}
              receiverAddress={customRecipient ?? undefined}
              receiverMatchesCalldata={recipientMatchesCalldata}
            />
          )}

          <div style={{ display: 'relative' }}>
            <SwapSection>
              <Trace section={InterfaceSectionName.CURRENCY_INPUT_PANEL}>
                <SwapCurrencyInputPanel
                  label={
                    independentField === Field.OUTPUT && !showWrap ? <Trans>From (at most)</Trans> : <Trans>From</Trans>
                  }
                  disabled={disableTokenInputs}
                  value={formattedAmounts[Field.INPUT]}
                  showMaxButton={showMaxButton}
                  currency={currencies[Field.INPUT] ?? null}
                  onUserInput={handleTypeInput}
                  onMax={handleMaxInput}
                  fiatValue={showFiatValueInput ? fiatValueInput : undefined}
                  onCurrencySelect={handleInputSelect}
                  otherCurrency={currencies[Field.OUTPUT]}
                  showCommonBases
                  id={InterfaceSectionName.CURRENCY_INPUT_PANEL}
                  loading={independentField === Field.OUTPUT && routeIsSyncing}
                />
              </Trace>
            </SwapSection>
            <ArrowWrapper clickable={isSupportedChain(chainId)}>
              <TraceEvent
                events={[BrowserEvent.onClick]}
                name={SwapEventName.SWAP_TOKENS_REVERSED}
                element={InterfaceElementName.SWAP_TOKENS_REVERSE_ARROW_BUTTON}
              >
                <ArrowContainer
                  data-testid="swap-currency-button"
                  onClick={() => {
                    !disableTokenInputs && onSwitchTokens()
                  }}
                  color={theme.textPrimary}
                >
                  <ArrowDown
                    size="16"
                    color={currencies[Field.INPUT] && currencies[Field.OUTPUT] ? theme.textPrimary : theme.textTertiary}
                  />
                </ArrowContainer>
              </TraceEvent>
            </ArrowWrapper>
          </div>
          <AutoColumn gap="md">
            <div>
              <OutputSwapSection>
                <Trace section={InterfaceSectionName.CURRENCY_OUTPUT_PANEL}>
                  <SwapCurrencyInputPanel
                    value={formattedAmounts[Field.OUTPUT]}
                    disabled={disableTokenInputs}
                    onUserInput={handleTypeOutput}
                    label={
                      independentField === Field.INPUT && !showWrap ? <Trans>To (at least)</Trans> : <Trans>To</Trans>
                    }
                    showMaxButton={false}
                    hideBalance={false}
                    fiatValue={showFiatValueOutput ? fiatValueOutput : undefined}
                    priceImpact={stablecoinPriceImpact}
                    currency={currencies[Field.OUTPUT] ?? null}
                    onCurrencySelect={handleOutputSelect}
                    otherCurrency={currencies[Field.INPUT]}
                    showCommonBases
                    disabledInput={true}
                    id={InterfaceSectionName.CURRENCY_OUTPUT_PANEL}
                    loading={independentField === Field.INPUT && routeIsSyncing}
                  />
                </Trace>
              </OutputSwapSection>
            </div>

            {receiverEnabled && showRecipientInput && !showWrap && (
              <ReceiverSection
                recipient={recipient || ''}
                onChangeRecipient={(value) => {
                  const trimmed = value.trim()
                  if (!trimmed) {
                    changeRecipient(null)
                    return
                  }
                  if (account && trimmed.toLowerCase() === account.toLowerCase()) {
                    changeRecipient(null)
                    return
                  }
                  changeRecipient(trimmed)
                }}
                onShowWarning={handleOpenRecipientWarning}
                onClose={handleCloseRecipientPanel}
                onRefetchQuote={refetchQuote}
              />
            )}

            {receiverEnabled && customRecipient && !showRecipientInput && !showWrap && (
              <ReceiverCollapsed
                recipient={customRecipient}
                onClick={() => setShowRecipientInput(true)}
                onRemove={handleRemoveRecipient}
                showWarning
              />
            )}

            {showDetailsDropdown && (
              <SwapDetailsDropdown
                trade={trade}
                syncing={routeIsSyncing}
                loading={routeIsLoading}
                allowedSlippage={allowedSlippage}
              />
            )}
            {showPriceImpactWarning && <PriceImpactWarning priceImpact={largerPriceImpact} />}
            <div>
              {swapIsUnsupported ? (
                <ButtonPrimary disabled={true}>
                  <ThemedText.DeprecatedMain mb="4px">
                    <Trans>Unsupported Asset</Trans>
                  </ThemedText.DeprecatedMain>
                </ButtonPrimary>
              ) : !account ? (
                <TraceEvent
                  events={[BrowserEvent.onClick]}
                  name={InterfaceEventName.CONNECT_WALLET_BUTTON_CLICKED}
                  properties={{ received_swap_quote: getIsValidSwapQuote(trade, tradeState, swapInputError) }}
                  element={InterfaceElementName.CONNECT_WALLET_BUTTON}
                >
                  <ButtonLight onClick={toggleWalletDrawer} fontWeight={600}>
                    <Trans>Connect Wallet</Trans>
                  </ButtonLight>
                </TraceEvent>
              ) : chainId && chainId !== connectedChainId ? (
                <ButtonPrimary
                  onClick={async () => {
                    try {
                      await switchChain(connector, chainId)
                    } catch (error) {
                      if (didUserReject(error)) {
                        // Ignore error, which keeps the user on the previous chain.
                      } else {
                        // TODO(WEB-3306): This UX could be improved to show an error state.
                        throw error
                      }
                    }
                  }}
                >
                  Connect to {getChainInfo(chainId)?.label}
                </ButtonPrimary>
              ) : showWrap ? (
                <ButtonPrimary
                  disabled={Boolean(wrapInputError)}
                  onClick={onWrap}
                  fontWeight={600}
                  data-testid="wrap-button"
                >
                  {wrapInputError ? (
                    <WrapErrorText wrapInputError={wrapInputError} />
                  ) : wrapType === WrapType.WRAP ? (
                    <Trans>Wrap</Trans>
                  ) : wrapType === WrapType.UNWRAP ? (
                    <Trans>Unwrap</Trans>
                  ) : null}
                </ButtonPrimary>
              ) : routeNotFound && userHasSpecifiedInputOutput && !routeIsLoading && !routeIsSyncing ? (
                <ButtonFetch onClick={refetchQuote} fontWeight={500} data-testid="refetch-button">
                  <Trans>fetching in progress, try refetch now</Trans>
                  <Trans>
                    <RefetchButton />
                  </Trans>
                </ButtonFetch>
              ) : (
                <ButtonError
                  onClick={() => {
                    setSwapState({
                      tradeToConfirm: trade,
                      attemptingTxn: false,
                      swapError: undefined,
                      showConfirm: true,
                      txHash: undefined,
                    })
                  }}
                  id="swap-button"
                  data-testid="swap-button"
                  disabled={!isValid || routeIsSyncing || routeIsLoading || priceImpactTooHigh}
                  error={isValid && priceImpactSeverity > 2 && allowance.state === AllowanceState.ALLOWED}
                >
                  <Text fontSize={18} fontWeight={600}>
                    {swapInputError ? (
                      swapInputError
                    ) : routeIsSyncing || routeIsLoading ? (
                      <Trans>Swap</Trans>
                    ) : priceImpactTooHigh ? (
                      <Trans>Price Impact Too High</Trans>
                    ) : priceImpactSeverity > 2 ? (
                      <Trans>Swap Anyway</Trans>
                    ) : (
                      <Trans>Swap</Trans>
                    )}
                  </Text>
                </ButtonError>
              )}
              {Boolean(isExpertMode && swapError) && <SwapCallbackError error={swapError?.message} />}
            </div>
          </AutoColumn>
        </SwapWrapper>
        <NetworkAlert />
      </SwapCardColumn>
      <RoutePanel $open={routePanelOpen} aria-hidden={!routePanelOpen}>
        {routePanelOpen &&
          (canShowRoutePanel ? (
            <SwapRoute
              trade={trade}
              syncing={routeIsSyncing}
              refreshing={routeIsRefreshing}
              canClose={Boolean(trade)}
              onClose={handleRoutePanelClose}
            />
          ) : (
            <ThemedText.BodySmall color="textSecondary" fontSize="13px">
              <Trans>Select tokens to view the route.</Trans>
            </ThemedText.BodySmall>
          ))}
      </RoutePanel>
    </SwapLayout>
  )
}
